import React from 'react';
import styles from '../insertsPages.module.css';
import { SelectionOption } from '../../../SelectionOption';
import { usePage } from '../../../../context/PageContext';
import { getPageOptions, resolveClicksToTargets } from '../../../../context/pages';

type OptionSelectionOption = {
  icon?: string;
  title?: string;
  subtitle?: string;
  getSubtitle?: () => string;
  clicksTo?: unknown;
  disabled?: boolean;
};

type OptionSelectionPageProps = {
  pageName: string;
  headerTitle?: string;
  subtitleOverrides?: Record<number, string>;
  hiddenOptionIndexes?: Set<number>;
};

/**
 * Generic OptionSelectionPage component that renders selection options based on page configuration.
 * This replaces individual page components like AddPersonalTouch, ChooseInsertPersonalisation, etc.
 */
export function OptionSelectionPage({
  pageName,
  headerTitle,
  subtitleOverrides,
  hiddenOptionIndexes
}: OptionSelectionPageProps) {
  const { getPageSelection, setPageSelection, goToPage } = usePage();
  const selectedOptionIndex = getPageSelection(pageName);

  const handleSelectOption = ({
    index,
    option
  }: {
    index: number;
    option: OptionSelectionOption;
  }) => {
    setPageSelection(pageName, index);

    // Navigate to the option destination, matching the config-driven footer behavior.
    const [destination] = resolveClicksToTargets(option?.clicksTo);
    if (typeof destination !== 'string' || destination.length === 0) {
      return;
    }

    setTimeout(() => {
      goToPage(destination);
    }, 300);
  };

  // Get options from centralized configuration.
  const options = getPageOptions(pageName) as OptionSelectionOption[];

  return (
    <div className={styles['inserts-pages--page']}>
      <div className={styles['page--container']}>
        {headerTitle && (
          <div className={styles['inserts-pages--header']}>
            <h1 className={styles['inserts-pages--title']}>{headerTitle}</h1>
          </div>
        )}
        <div className={styles['inserts-pages--options']}>
          {options.map((option, index) => {
            if (hiddenOptionIndexes?.has(index)) {
              return null;
            }

            // MVVM override takes precedence; fall back to static subtitle or legacy getSubtitle().
            const subtitle =
              subtitleOverrides?.[index] ??
              (typeof option.getSubtitle === 'function' ? option.getSubtitle() : option.subtitle);

            return (
              <SelectionOption
                key={index}
                icon={option.icon}
                title={option.title}
                subtitle={subtitle}
                isSelected={selectedOptionIndex === index}
                onClick={() => handleSelectOption({ index, option })}
                clicksTo={option.clicksTo}
                disabled={option.disabled}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
